import styled from "styled-components"
import { useDispatch, useSelector } from "react-redux"
import { Tooltip as ReactTooltip } from "react-tooltip"
import Portal from "react-overlays/Portal"
import { RowNode } from "ag-grid-community"
import { buildLaborRateMatchContextParams } from "../../../rate-tables/utils"
import { useRowData } from "../../../common/ag-grid-ts-utils"
import { selectLaborRatesById } from "../../../rate-tables/selectors"
import {
    AppDispatch,
    BasicTkTimeType,
    LaborRateMatch,
    TimekeepingEntryModalRowData,
    TkTimeType,
} from "../../../common/types"
import {
    colorAttentionBlue10,
    colorAttentionBlue50,
    colorAttentionGreen10,
    colorAttentionGreen50,
    colorFunctionalWhite,
    fontSizeS,
    fontSizeXs,
    spacingXs,
} from "@rhumbix/rmbx_design_system_web"
import { zIndex } from "../../../common/styles"
import { useState, useEffect } from "react"
import { referenceableDataSelector } from "../../../selectors"
import { checkLaborRateMatch } from "../../../rate-tables/actions"
import { tEntitySelection } from "../../../cached-data/types"

/**
 * Props for the LaborRateMatchBadge component.
 */
interface LaborRateMatchBadgeProps {
    /** The AG Grid row node - use node.data to get current row data */
    node: RowNode
    /** The time type to check for rate matches: ST, OT, or DT */
    timeType: Exclude<TkTimeType, "TT">
}

/** Possible sources for a rate match. */
type RateMatchSource = "clientside_match" | "clientside_override" | "saved_match" | "saved_override"

/**
 * A rate ID and where it came from so the UI can render something useful.
 */
interface RateMatchDisplayInfo {
    /** The source of the rate match. */
    source: RateMatchSource
    /** The rate ID to use for UI */
    rateId: number
}

/**
 * Determines the match type and rate ID based on priority rules:
 *
 * 1. Clientside override (number): User picked a specific rate → clientside_override
 * 2. Clientside override (null): User reset, auto-match masks saved → clientside_match
 * 3. Saved rate match with override: → saved_override
 * 4. Saved rate match: → saved_match
 * 5. Default auto-match: → clientside_match
 *
 * @param overrideRateId - The clientside override LaborRate ID (number), reset (null), or untouched (undefined)
 * @param cachedRateMatch - The cached LaborRateMatch record looked up by savedRateMatchId, if available
 * @param autoMatchRateId - The clientside auto-matched LaborRate ID, if any
 * @returns The rate match display info, or null if no rate match should be shown
 */
const getRateMatchDisplayInfo = (
    overrideRateId: number | null | undefined,
    cachedRateMatch: LaborRateMatch | undefined,
    autoMatchRateId: number | null
): RateMatchDisplayInfo | null => {
    // Case 1: User explicitly selected a rate via the reprice workflow
    if (typeof overrideRateId === "number") {
        return { source: "clientside_override", rateId: overrideRateId }
    }

    // Case 2: User chose to reset the assigned rate — auto-match takes over and masks any saved match
    if (overrideRateId === null) {
        return autoMatchRateId ? { source: "clientside_match", rateId: autoMatchRateId } : null
    }

    // Cases 3/4: No clientside override — check for a saved rate match on the work component.
    if (cachedRateMatch) {
        if (cachedRateMatch.labor_rate_override) {
            return { source: "saved_override", rateId: cachedRateMatch.labor_rate_override }
        }
        if (cachedRateMatch.labor_rate) {
            return { source: "saved_match", rateId: cachedRateMatch.labor_rate }
        }
    }

    // Case 5: No override, no saved match — show default auto-match if available
    if (autoMatchRateId) {
        return { source: "clientside_match", rateId: autoMatchRateId }
    }

    return null
}

/**
 * Returns the clientside override LaborRate ID for the given time type from the row data.
 *
 * @param rowData - The timekeeping entry modal row data
 * @param timeType - ST, OT, or DT
 * @returns number if user selected a rate, null if user reset, undefined if untouched
 */
const getOverrideRateId = (
    rowData: TimekeepingEntryModalRowData | undefined,
    timeType: BasicTkTimeType
): number | null | undefined => {
    if (!rowData) return undefined
    if (timeType === "ST") return rowData.labor_rate_st_override
    if (timeType === "OT") return rowData.labor_rate_ot_override
    if (timeType === "DT") return rowData.labor_rate_dt_override
    return undefined
}

/**
 * Returns the saved LaborRateMatch ID for the given time type from the row data.
 *
 * @param rowData - The timekeeping entry modal row data
 * @param timeType - ST, OT, or DT
 * @returns The saved LaborRateMatch ID, or undefined if none
 */
const getSavedRateMatchId = (
    rowData: TimekeepingEntryModalRowData | undefined,
    timeType: Exclude<TkTimeType, "TT">
): number | undefined => {
    if (!rowData) return undefined
    if (timeType === "ST") return rowData.rate_match_st
    if (timeType === "OT") return rowData.rate_match_ot
    if (timeType === "DT") return rowData.rate_match_dt
    return undefined
}

/**
 * Badge component that displays predicted or applied labor rate information.
 * Shows a color-coded badge with a tooltip containing the Rate Table and Labor Rate names.
 *
 * Badge state priority (highest to lowest):
 * 1. Clientside override (user selected a rate)
 * 2. Clientside reprice masking saved match (use clientside auto-match)
 * 3. Saved rate match override
 * 4. Saved rate match (auto-matched)
 * 5. Default clientside auto-match
 */
const LaborRateMatchBadge: React.FC<LaborRateMatchBadgeProps> = ({ node, timeType }) => {
    const dispatch = useDispatch() as AppDispatch
    const cachedLaborRatesById = useSelector(selectLaborRatesById)
    const referenceableData: tEntitySelection = useSelector(referenceableDataSelector)
    const [autoMatchRateId, setAutoMatchRateId] = useState<number | null>(null)
    // Trigger re-renders when AG-Grid updates the node data.
    const rowData = useRowData(node) as TimekeepingEntryModalRowData | undefined

    // Row data changes a lot, so we'll keep track of just the fields we're interested in to better detect
    // when matching needs to be re-run.
    const { employeeId, projectId, costCodeId, modifiersString, entryDate } = rowData
        ? buildLaborRateMatchContextParams(rowData, referenceableData)
        : { employeeId: null, projectId: null, costCodeId: null, modifiersString: "", entryDate: "" }

    // When it looks like relevant row data has changed, re-run matching.
    useEffect(
        () => {
            const runMatching = async () => {
                const matchId = await dispatch(
                    checkLaborRateMatch({ employeeId, projectId, costCodeId, modifiersString, entryDate }, timeType)
                )
                setAutoMatchRateId(matchId)
            }
            runMatching()
        },
        // These are all primitive values so that the effect only runs when they change.
        // Avoid tracking objects that may get rebuilt every render here.
        [employeeId, projectId, costCodeId, modifiersString, entryDate, timeType]
    )

    const overrideRateId = getOverrideRateId(rowData, timeType)
    const savedRateMatchId = getSavedRateMatchId(rowData, timeType)
    const cachedRateMatch = savedRateMatchId
        ? (referenceableData.laborRateMatches[savedRateMatchId] as LaborRateMatch | undefined)
        : undefined
    const displayInfo = getRateMatchDisplayInfo(overrideRateId, cachedRateMatch, autoMatchRateId)

    if (!displayInfo) {
        return null
    }

    const { source, rateId } = displayInfo
    const cachedRate = rateId ? cachedLaborRatesById[rateId] : undefined
    const autoMatchRate = autoMatchRateId ? cachedLaborRatesById[autoMatchRateId] : undefined
    const showVersionNotice =
        source === "saved_match" && cachedRate && autoMatchRate && cachedRate.version !== autoMatchRate.version
    const rateName = cachedRate?.name ?? "Rate applied"
    const rateTableName = cachedRate?.rate_table?.name ?? ""
    const tooltipId = `labor-rate-match-${rowData?.id ?? node.id ?? "unknown"}-${timeType}`

    return (
        <>
            <MatchBadge source={source} data-tooltip-id={tooltipId} />
            <Portal container={document.body}>
                <ReactTooltip
                    id={tooltipId}
                    place="top"
                    float={true}
                    variant="dark"
                    style={{ zIndex: zIndex.Tooltip }}
                >
                    <TooltipContent>
                        <RateName>{rateName}</RateName>
                        {rateTableName ? <RateTableName>{rateTableName}</RateTableName> : null}
                        <RateStatus>
                            {source === "saved_override"
                                ? "Override"
                                : source === "saved_match"
                                ? "Saved"
                                : source === "clientside_override"
                                ? "Override (pending) "
                                : "Pending"}
                        </RateStatus>
                        {showVersionNotice ? <VersionNotice>New version available</VersionNotice> : null}
                    </TooltipContent>
                </ReactTooltip>
            </Portal>
        </>
    )
}

interface MatchBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
    source: RateMatchSource
}

const BADGE_STYLES: Record<RateMatchSource, { background: string; border: string; color: string }> = {
    clientside_match: {
        background: colorAttentionBlue10,
        border: `1px solid ${colorAttentionBlue50}`,
        color: colorAttentionBlue50,
    },
    clientside_override: {
        background: colorAttentionGreen10,
        border: `1px solid ${colorAttentionGreen50}`,
        color: colorAttentionGreen50,
    },
    saved_match: {
        background: colorAttentionBlue50,
        border: `1px solid ${colorAttentionBlue50}`,
        color: colorFunctionalWhite,
    },
    saved_override: {
        background: colorAttentionGreen50,
        border: `1px solid ${colorAttentionGreen50}`,
        color: colorFunctionalWhite,
    },
}

/**
 * A circular badge indicator for labor rate match status.
 * Styling differs depending on the source of the rate match.
 *
 * @param source - The source of the rate match
 */
const MatchBadge: React.FC<MatchBadgeProps> = ({ source, ...rest }) => (
    <MatchBadgeHitArea {...rest}>
        <StyledMatchBadge source={source}>
            <span>$</span>
        </StyledMatchBadge>
    </MatchBadgeHitArea>
)

/** Square hit area wrapper that makes the tooltip trigger on the full bounding box, not just the circle. */
const MatchBadgeHitArea = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
`

const StyledMatchBadge = styled.div<{ source: RateMatchSource }>`
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: ${({ source }) => BADGE_STYLES[source].background};
    border: ${({ source }) => BADGE_STYLES[source].border};
    color: ${({ source }) => BADGE_STYLES[source].color};
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${fontSizeXs};
    font-weight: 400;
    line-height: 1;
    box-sizing: border-box;
    pointer-events: none;

    span {
        display: block;
        line-height: 1;
        transform: translateY(0.25px);
    }
`

const TooltipContent = styled.div`
    display: flex;
    flex-direction: column;
`

const VersionNotice = styled.span`
    font-size: ${fontSizeXs};
    font-style: italic;
`

const RateStatus = styled.span`
    font-size: ${fontSizeXs};
    font-style: italic;
    margin-top: ${spacingXs};
`

const RateTableName = styled.span`
    font-size: ${fontSizeXs};
`

const RateName = styled.span`
    font-size: ${fontSizeS};
`

export default LaborRateMatchBadge
